import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { EXAM_CAPACITY_MAX, missingForSchedule, type ExamSessionInput, type ExamSessionView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DatePicker } from '../../../components/DatePicker';
import { Drawer } from '../../../components/Drawer';
import { Field, FieldRow } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { TimePicker } from '../../../components/TimePicker';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { wholeNumber } from '../../../lib/whole-number';
import { useToasts } from '../../../stores/toasts';
import { useJobs } from '../../jobs/api/jobs-api';
import { EXAM_SESSIONS_KEY, dhakaToIso, examSessionKey, examSessionsApi, isoToDhaka, useInvigilators } from '../api/exam-sessions-api';
import styles from './Assessments.module.css';

interface SessionEditorProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Null for a new session. */
  readonly session: ExamSessionView | null;
  /** A new session from a job's page knows its job; from the list it is chosen here. */
  readonly jobId?: string;
}

interface Draft {
  readonly jobId: string | null;
  readonly name: string;
  readonly date: string;
  readonly time: string;
  readonly venue: string;
  readonly capacity: number;
  readonly invigilatorUserId: string | null;
  readonly note: string;
}

const DEFAULT_CAPACITY = 20;
const JOB_PICKER_SIZE = 200;

function draftOf(session: ExamSessionView | null, jobId: string | undefined): Draft {
  const when = isoToDhaka(session?.startsAt ?? null);
  return {
    jobId: session?.jobId ?? jobId ?? null,
    name: session?.name ?? '',
    date: when.date,
    time: when.time,
    venue: session?.venue ?? '',
    capacity: session?.capacity ?? DEFAULT_CAPACITY,
    invigilatorUserId: session?.invigilatorUserId ?? null,
    note: session?.note ?? '',
  };
}

/**
 * ASM-04's form (F9.08), in a side panel so the list stays in view. With a date, a venue
 * and an invigilator the session is scheduled at once (§L25); without, it waits as a
 * draft. On a scheduled session the date and venue change by "Move", which tells everyone.
 */
export function SessionEditor(props: SessionEditorProps): ReactNode {
  if (!props.isOpen) return null;
  return <Editor key={props.session?.id ?? 'new'} {...props} />;
}

function Editor({ isOpen, onClose, session, jobId }: SessionEditorProps): ReactNode {
  const [draft, setDraft] = useState<Draft>(() => draftOf(session, jobId));
  const [fields, setFields] = useState<Readonly<Record<string, string>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const jobs = useJobs({ page: 1, pageSize: JOB_PICKER_SIZE, status: 'open' });
  const invigilators = useInvigilators(draft.jobId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const isDraft = !session || session.status === 'draft';
  const startsAt = draft.date && draft.time ? dhakaToIso(draft.date, draft.time) : null;
  const input: ExamSessionInput = {
    name: draft.name.trim() || null,
    venue: draft.venue.trim() || null,
    startsAt,
    capacity: draft.capacity,
    invigilatorUserId: draft.invigilatorUserId,
    note: draft.note.trim() || null,
  };
  const missing = missingForSchedule(input);
  const isValid = draft.jobId !== null && draft.capacity >= 1 && (session === null || draft.capacity >= session.booked);

  function change(patch: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function onSave(): Promise<void> {
    if (!draft.jobId) return;
    setFields({});
    setIsSaving(true);
    try {
      const saved = session ? await examSessionsApi.update(session.id, session.version, input) : await examSessionsApi.create(draft.jobId, input);
      queryClient.setQueryData(examSessionKey(saved.id), saved);
      await queryClient.invalidateQueries({ queryKey: EXAM_SESSIONS_KEY });
      show({
        tone: 'success',
        message: saved.status === 'draft' ? 'Saved as a draft' : session ? 'Session saved' : 'Exam session scheduled',
        detail: saved.status === 'draft' ? `It is scheduled once it has ${missingForSchedule(input).join(', ')}.` : saved.isShortNotice ? 'It is less than a day away, so its list stays open until you lock it or the exam starts.' : undefined,
      });
      onClose();
      if (!session) void navigate({ to: '/assessments/sessions/$sessionId', params: { sessionId: saved.id } });
    } catch (cause) {
      setFields(cause instanceof ApiError ? cause.fields : {});
      show({ tone: 'danger', message: 'The session was not saved', detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  }

  const fieldError = (name: string): string | undefined => fields[`session.${name}`] ?? fields[name];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={session ? 'Edit exam session' : 'New exam session'}
      meta={session ? session.jobTitle : 'An on-site written exam for one job'}
      footer={
        <div className={styles.drawerFooter}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" disabled={!isValid} isPending={isSaving} onClick={() => void onSave()}>
            {session ? 'Save' : missing.length === 0 ? 'Schedule' : 'Save as draft'}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {!session && !jobId && (
          <Field label="Job" isRequired hint="Its test must be one sat on paper (job setup, step 5)." error={fieldError('jobId')}>
            {({ inputId, describedBy, isInvalid }) => (
              <Select
                id={inputId}
                describedBy={describedBy}
                isInvalid={isInvalid}
                label="Job"
                placeholder="Choose a job"
                value={draft.jobId}
                options={(jobs.data?.rows ?? []).map((job) => ({ value: job.id, label: job.title, description: job.reference }))}
                onChange={(value) => change({ jobId: value, invigilatorUserId: null })}
              />
            )}
          </Field>
        )}
        <Field label="Name" isOptional hint="Left empty, it is named after the job.">
          {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} value={draft.name} placeholder="Accounts Officer exam — Saturday" onChange={(event) => change({ name: event.target.value })} />}
        </Field>
        <FieldRow>
          <Field label="Date" hint={isDraft ? 'Dhaka time.' : 'Use "Move" to change it.'} error={fieldError('startsAt')}>
            {({ inputId, describedBy, isInvalid }) => (
              <DatePicker id={inputId} label="Date" describedBy={describedBy} isInvalid={isInvalid} disabled={!isDraft} value={draft.date} onChange={(date) => change({ date })} />
            )}
          </Field>
          <Field label="Start time">
            {({ inputId }) => <TimePicker id={inputId} label="Start time" disabled={!isDraft} value={draft.time} onChange={(time) => change({ time })} />}
          </Field>
        </FieldRow>
        <Field label="Venue" error={fieldError('venue')}>
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={!isDraft} value={draft.venue} placeholder="Gulshan office, 4th floor training room" onChange={(event) => change({ venue: event.target.value })} />
          )}
        </Field>
        <FieldRow>
          <Field label="Seats" isRequired error={fieldError('capacity') ?? (session && draft.capacity < session.booked ? `${session.booked} people are already booked.` : undefined)}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isInvalid={isInvalid}
                isNumeric
                inputMode="numeric"
                value={String(draft.capacity)}
                onChange={(event) => change({ capacity: Math.min(EXAM_CAPACITY_MAX, wholeNumber(event.target.value)) })}
              />
            )}
          </Field>
          <Field label="Invigilator" error={fieldError('invigilatorUserId')}>
            {({ inputId, describedBy, isInvalid }) => (
              <Select
                id={inputId}
                describedBy={describedBy}
                isInvalid={isInvalid}
                label="Invigilator"
                placeholder={draft.jobId ? 'Choose who supervises' : 'Choose the job first'}
                disabled={draft.jobId === null}
                value={draft.invigilatorUserId}
                options={(invigilators.data ?? []).map((person) => ({ value: person.id, label: person.fullName, description: person.roleLabel }))}
                onChange={(value) => change({ invigilatorUserId: value })}
              />
            )}
          </Field>
        </FieldRow>
        <Field label="Note for the invigilator" isOptional>
          {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} rows={2} value={draft.note} placeholder="Collect phones at the door." onChange={(event) => change({ note: event.target.value })} />}
        </Field>
        {isDraft && missing.length > 0 && <p className={styles.hint}>Saved as a draft until it has {missing.join(', ')}.</p>}
      </div>
    </Drawer>
  );
}
